import BASE_URL from "../../config/api";
import { useState, useEffect } from "react";
import { BiUser } from "react-icons/bi";
import { AiOutlineMail } from "react-icons/ai";
import { BsFillTelephoneFill, BsWhatsapp, BsStarFill, BsX } from "react-icons/bs";
import { IoMdChatbubbles } from "react-icons/io";
import { MdVerified } from "react-icons/md";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import ImageSkeleton from "./ImageSkeleton";

const deliverySteps = [
  { icon: "💳", label: "Purchase online via Paystack" },
  { icon: "📬", label: "Receive plan by email or courier" },
  { icon: "📋", label: "Ready for regulatory submission" },
];

function HouseDetails() {
  const [loading, setLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [payModal, setPayModal] = useState(false);
  const [payEmail, setPayEmail] = useState("");
  const [payName, setPayName] = useState("");
  const [verifying, setVerifying] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const [formFields, setFormFields] = useState({
    fullName: "",
    emailAddress: "",
    phoneNumber: "",
    comment: "",
  });

  const runOnStart = async () => {
    setLoading(true);
    await axios
      .get(`${BASE_URL}/drawing/get-drawings/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
      })
      .then((response) => {
        const data = response.data.data;
        setSelectedDrawing(data);
        setActiveImage(data.frontElevation);
        localStorage.setItem("drawing_id", data._id);
      })
      .catch(() => toast.error("Failed to load plan details."));
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runOnStart(); }, []);

  const priceInKobo = () => {
    const raw = Number((selectedDrawing?.price || "").toString().replace(/[^0-9.]/g, "")) || 0;
    return raw * 100;
  };

  const openPayModal = () => {
    // Pre-fill from enquiry form if already typed
    setPayName(formFields.fullName || "");
    setPayEmail(formFields.emailAddress || "");
    setPayModal(true);
  };

  const payWithPaystack = () => {
    if (!payEmail.trim()) return toast.error("Please enter your email address");
    const paystackKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
    if (!paystackKey || paystackKey.startsWith("pk_test_xxx")) {
      return toast.error("Paystack key is not configured. Check your .env file.");
    }
    if (!window.PaystackPop) {
      return toast.error("Paystack script failed to load. Please refresh the page.");
    }
    let handler;
    try {
      handler = window.PaystackPop.setup({
      key: paystackKey,
      email: payEmail.trim(),
      amount: priceInKobo(),
      currency: "NGN",
      ref: `PS-${selectedDrawing.refNo}-${Date.now()}`,
      metadata: {
        custom_fields: [
          { display_name: "Plan", variable_name: "plan", value: selectedDrawing.description },
          { display_name: "Reference No", variable_name: "ref_no", value: selectedDrawing.refNo },
          { display_name: "Customer Name", variable_name: "name", value: payName },
        ],
      },
      callback: (response) => {
        setPayModal(false);
        setVerifying(true);
        axios
          .post(`${BASE_URL}/payment/verify`, { reference: response.reference })
          .catch((err) => {
            console.error("Payment verify error:", err?.response?.data ?? err.message);
          })
          .finally(() => {
            setVerifying(false);
            navigate(
              `/payment-confirmation?ref=${response.reference}&plan=${encodeURIComponent(selectedDrawing.description)}&refNo=${selectedDrawing.refNo}&amount=${priceInKobo() / 100}`
            );
          });
      },
      onClose: () => {},
    });
    } catch (err) {
      console.error("PaystackPop.setup error:", err);
      return toast.error("Could not initialise payment. Please try again.");
    }
    handler.openIframe();
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormFields((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setCommentLoading(true);
    const drawingId = localStorage.getItem("drawing_id");
    try {
      const response = await axios.post(
        `${BASE_URL}/comment/add-comment`,
        { ...formFields, drawingId }
      );
      if (response.status === 200) {
        runOnStart();
        toast.success("Enquiry sent successfully!");
        setFormFields({ fullName: "", emailAddress: "", phoneNumber: "", comment: "" });
      } else {
        toast.error("Submission failed. Please try again.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    } finally {
      setCommentLoading(false);
      setFormFields({ fullName: "", emailAddress: "", phoneNumber: "", comment: "" });
    }
  };

  const elevations = selectedDrawing
    ? [
        { src: selectedDrawing.frontElevation, label: "Front Elevation" },
        { src: selectedDrawing.leftElevation, label: "Left Elevation" },
        { src: selectedDrawing.rightElevation, label: "Right Elevation" },
      ].filter((e) => e.src)
    : [];

  return (
    <div className="font-Montserrat min-h-screen bg-gray-50">
      <ToastContainer position="top-right" />

      {/* Page top spacer for fixed navbar */}
      <div className="h-[56px] md:h-[72px]" />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 px-4 md:px-10 py-3">
        <div className="max-w-6xl mx-auto text-sm text-gray-500 flex items-center gap-2">
          <Link to="/" className="hover:text-[#1877F2] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/featured-house-plans" className="hover:text-[#1877F2] transition-colors">Plans</Link>
          <span>/</span>
          <span className="text-gray-800 font-medium truncate max-w-[200px]">
            {selectedDrawing?.refNo || "Plan Detail"}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="max-w-6xl mx-auto px-4 md:px-10 py-10 min-h-screen">
          <ImageSkeleton />
        </div>
      ) : selectedDrawing ? (
        <>
          {/* Main content */}
          <div className="max-w-6xl mx-auto px-4 md:px-10 py-8">
            <div className="flex flex-col lg:flex-row gap-8">

              {/* Left — image gallery */}
              <div className="lg:w-3/5 w-full">
                {/* Main image */}
                <div className="rounded-2xl overflow-hidden shadow-md bg-white">
                  <img
                    src={activeImage || selectedDrawing.frontElevation}
                    alt={selectedDrawing.description}
                    className="w-full h-[280px] md:h-[420px] object-cover"
                  />
                </div>

                {/* Thumbnails */}
                {elevations.length > 1 && (
                  <div className="flex gap-3 mt-3">
                    {elevations.map(({ src, label }) => (
                      <button
                        key={label}
                        onClick={() => setActiveImage(src)}
                        className={`flex-1 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                          activeImage === src
                            ? "border-[#1877F2] shadow-md"
                            : "border-transparent opacity-70 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={src}
                          alt={label}
                          className="w-full h-20 md:h-28 object-cover"
                        />
                        <p className="text-[10px] text-gray-500 text-center py-1 bg-white font-medium uppercase tracking-wide">
                          {label}
                        </p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Floor plan details */}
                {selectedDrawing.drawing_details?.length > 0 && (
                  <div className="mt-6 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <h3 className="font-bold text-gray-900 text-base md:text-lg mb-5 flex items-center gap-2">
                      <span className="w-1 h-5 bg-[#1877F2] rounded-full inline-block" />
                      Floor Plan Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {selectedDrawing.drawing_details.map((detail, i) => (
                        <div key={i} className="bg-gray-50 rounded-xl p-4">
                          <h4 className="font-bold text-[#1877F2] uppercase text-sm mb-3 tracking-wide">
                            {detail.floor}
                          </h4>
                          <ul className="space-y-1.5">
                            {detail.details.split(",").map((item, j) => (
                              <li key={j} className="text-gray-700 text-sm flex items-start gap-2">
                                <span className="text-[#1877F2] mt-0.5 flex-shrink-0">✓</span>
                                <span className="uppercase">{item.trim()}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right — sticky info sidebar */}
              <div className="lg:w-2/5 w-full">
                <div className="lg:sticky lg:top-24 space-y-4">

                  {/* Plan info card */}
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold text-[#1877F2] bg-[#FFF8EC] px-3 py-1 rounded-full uppercase tracking-wide">
                        {selectedDrawing.refNo}
                      </span>
                      <MdVerified className="text-[#1877F2] text-lg" title="Verified Design" />
                    </div>
                    <h1 className="text-lg md:text-xl font-bold text-gray-900 leading-snug mb-4 uppercase">
                      {selectedDrawing.description}
                    </h1>

                    {/* Price */}
                    <div className="bg-[#FFF8EC] rounded-xl p-4 mb-5">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Plan Price</p>
                      <p className="text-2xl md:text-3xl font-bold text-[#1877F2]">
                        ₦{(Number((selectedDrawing.price || "").toString().replace(/[^0-9.]/g, "")) || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Includes professional stamp &amp; full documentation</p>
                    </div>

                    {/* Buy CTA */}
                    <button
                      onClick={openPayModal}
                      className="block w-full text-center bg-[#1877F2] hover:bg-[#0e5fc7] text-white font-bold py-3.5 rounded-full transition-colors duration-200 text-base mb-3"
                    >
                      Buy This Plan
                    </button>
                    <a
                      href={`https://wa.me/+2349033020343?text=Hello, I'm interested in plan ${selectedDrawing.refNo} - ${selectedDrawing.description}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full border-2 border-green-500 text-green-600 hover:bg-green-500 hover:text-white font-bold py-3 rounded-full transition-all duration-200 text-sm"
                    >
                      <BsWhatsapp />
                      Enquire on WhatsApp
                    </a>
                  </div>

                  {/* How you'll receive it */}
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2">
                      <span className="w-1 h-4 bg-[#1877F2] rounded-full inline-block" />
                      How You'll Receive This Plan
                    </h3>
                    <div className="space-y-3">
                      {deliverySteps.map(({ icon, label }) => (
                        <div key={label} className="flex items-center gap-3">
                          <span className="text-lg">{icon}</span>
                          <p className="text-gray-600 text-sm">{label}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trust signals */}
                  <div className="bg-[#1877F2] rounded-2xl p-5 text-white">
                    <div className="flex items-center gap-2 mb-3">
                      <BsStarFill />
                      <span className="font-bold text-sm">Why Buy From Us?</span>
                    </div>
                    <ul className="space-y-2 text-sm text-white/90">
                      <li className="flex gap-2"><span>✓</span> Professional &amp; regulatory-compliant designs</li>
                      <li className="flex gap-2"><span>✓</span> Over 10 years of architectural expertise</li>
                      <li className="flex gap-2"><span>✓</span> Nationwide delivery available</li>
                      <li className="flex gap-2"><span>✓</span> WhatsApp support throughout the process</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comments */}
          {selectedDrawing.comments?.length > 0 && (
            <div className="max-w-6xl mx-auto px-4 md:px-10 pb-8">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
                <h3 className="font-bold text-gray-900 text-lg mb-6 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#1877F2] rounded-full inline-block" />
                  Enquiries &amp; Comments ({selectedDrawing.comments.length})
                </h3>
                <div className="space-y-5">
                  {selectedDrawing.comments.map((comment, i) => (
                    <div key={i} className="flex gap-4 pb-5 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="w-9 h-9 rounded-full bg-[#FFF8EC] border border-[#1877F2] flex items-center justify-center text-[#1877F2] font-bold text-sm flex-shrink-0">
                        {comment.fullName?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{comment.fullName}</p>
                        <p className="text-gray-600 text-sm mt-1 leading-relaxed">{comment.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-24 text-gray-400">Plan not found.</div>
      )}

      {/* Verifying overlay */}
      {verifying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-4">
            <svg className="animate-spin h-10 w-10 text-[#1877F2]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="font-semibold text-gray-800 text-base">Confirming your payment…</p>
          </div>
        </div>
      )}

      {/* Payment email modal */}
      {payModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setPayModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
            <button onClick={() => setPayModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <BsX className="text-2xl" />
            </button>
            <h2 className="font-bold text-gray-900 text-lg mb-1">Complete Your Purchase</h2>
            <p className="text-gray-500 text-sm mb-5">
              You'll be charged{" "}
              <span className="font-bold text-[#1877F2]">
                ₦{(priceInKobo() / 100).toLocaleString()}
              </span>{" "}
              for <span className="font-semibold">{selectedDrawing?.description}</span>
            </p>
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name</label>
                <input
                  value={payName}
                  onChange={(e) => setPayName(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]/30 focus:border-[#1877F2]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address *</label>
                <input
                  value={payEmail}
                  onChange={(e) => setPayEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]/30 focus:border-[#1877F2]"
                />
                <p className="text-xs text-gray-400 mt-1">Your plan will be sent to this email</p>
              </div>
            </div>
            <button
              onClick={payWithPaystack}
              disabled={!payEmail.trim()}
              className="w-full bg-[#1877F2] hover:bg-[#0e5fc7] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-full transition-colors duration-200 text-base"
            >
              Pay ₦{(priceInKobo() / 100).toLocaleString()} →
            </button>
            <p className="text-center text-xs text-gray-400 mt-3">Secured by Paystack 🔒</p>
          </div>
        </div>
      )}

      {/* Enquiry form */}
      <div className="bg-gray-900 py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-10">
          <div className="text-center text-white mb-10">
            <p className="text-[#1877F2] uppercase tracking-widest text-xs font-semibold mb-2">
              Need Help?
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Send Us an Enquiry</h2>
            <p className="text-gray-400 text-sm">
              Ask about this plan or any of our services — we'll respond promptly.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <input
                  onChange={handleFieldChange}
                  type="text"
                  name="fullName"
                  value={formFields.fullName}
                  required
                  placeholder="Full Name"
                  className="w-full px-4 py-3 pr-11 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#1877F2] transition-colors"
                />
                <BiUser className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg" />
              </div>
              <div className="relative">
                <input
                  onChange={handleFieldChange}
                  type="email"
                  name="emailAddress"
                  value={formFields.emailAddress}
                  required
                  placeholder="Email Address"
                  className="w-full px-4 py-3 pr-11 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#1877F2] transition-colors"
                />
                <AiOutlineMail className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg" />
              </div>
              <div className="relative">
                <input
                  onChange={handleFieldChange}
                  type="tel"
                  name="phoneNumber"
                  value={formFields.phoneNumber}
                  required
                  placeholder="Phone Number"
                  className="w-full px-4 py-3 pr-11 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#1877F2] transition-colors"
                />
                <BsFillTelephoneFill className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg" />
              </div>
              <div className="relative md:row-span-2">
                <textarea
                  name="comment"
                  required
                  value={formFields.comment}
                  onChange={handleFieldChange}
                  placeholder="Your message or question..."
                  rows={5}
                  className="w-full h-full px-4 py-3 pr-11 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#1877F2] transition-colors resize-none"
                />
                <IoMdChatbubbles className="absolute right-4 top-4 text-gray-500 text-lg" />
              </div>
            </div>
            <button
              type="submit"
              disabled={commentLoading}
              className="w-full bg-[#1877F2] hover:bg-[#0e5fc7] disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-colors duration-200 text-base"
            >
              {commentLoading ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default HouseDetails;
